import {Call, Device} from '@twilio/voice-sdk'
import {Dialog, Modal, ModalOverlay} from 'react-aria-components'

import {useAccountInformationValue} from '@/auth/hooks/useAccountInformation'
import {useLocale} from '@/lib/locale/locale'
import {getPhoneInfoByLocale} from '@/lib/locale/phoneNumbers'

import {useTwilioToken} from './hooks/useTwilioToken'
import Keypad from './Keypad'
import style from './Phone.module.scss'
import Timer from './Timer'

enum USER_STATE {
  IDLE = 'IDLE',
  CALLING = 'CALLING',
  ON_CALL = 'ON_CALL',
}

interface PhoneProps {
  onClose?: () => void
}

const DUMMY_NOT_LOGGED_IN_EMAIL = 'f8KzP1qL@innosearch.ai'

export default memo(function Phone({onClose}: PhoneProps) {
  const [userState, setUserState] = useState<USER_STATE>(USER_STATE.IDLE)
  const connectionRef = useRef<Call | null>(null)
  const deviceRef = useRef<Device | null>(null)
  const keypadInputRef = useRef<HTMLInputElement>(null)
  const account = useAccountInformationValue()
  const [locale] = useLocale()
  const phoneInfo = getPhoneInfoByLocale(locale)
  const {data: token, error: tokenError} = useTwilioToken()

  if (tokenError) {
    console.error('Failed to get Twilio token:', tokenError)
  }

  const cleanupDevice = useStableCallback(() => {
    if (!deviceRef.current) return
    try {
      console.log('Cleaning up Twilio device')
      if (connectionRef.current) {
        connectionRef.current.disconnect()
        connectionRef.current = null
      }
      deviceRef.current.destroy()
      deviceRef.current = null
    } catch (error) {
      console.error('Error cleaning up device:', error)
    }
  })

  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        cleanupDevice()
      }
    }
  }, [])

  const resetCallState = useCallback(() => {
    const conn = connectionRef.current
    if (conn) {
      conn.removeAllListeners('accept')
      conn.removeAllListeners('disconnect')
      conn.removeAllListeners('reject')
      conn.removeAllListeners('cancel')
      conn.disconnect()
      connectionRef.current = null
    }

    setUserState(USER_STATE.IDLE)
    cleanupDevice()
  }, [])

  const createAndRegisterDevice = async () => {
    if (!token) {
      console.error('No twilio token was available')
      return null
    }
    try {
      const device = new Device(token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      })
      deviceRef.current = device
      device.on('error', (error: Error) => {
        console.error('Device error:', error)
        resetCallState()
      })
      await device.register()
      return device
    } catch (error) {
      console.error('Error creating/registering device:', error)
      resetCallState()
      return null
    }
  }

  const handleCall = async () => {
    if (userState === USER_STATE.ON_CALL && connectionRef.current) {
      connectionRef.current.disconnect()
      resetCallState()
      return
    }
    setUserState(USER_STATE.CALLING)
    try {
      const device = await createAndRegisterDevice()
      if (!device) return
      const params: {locale: string; email: string} = {
        locale,
        email: account?.email ?? DUMMY_NOT_LOGGED_IN_EMAIL,
      }
      const conn = await device.connect({params})
      // eslint-disable-next-line require-atomic-updates -- we assign the connectionRef immediately after connect()
      connectionRef.current = conn
      conn.on('accept', () => {
        setUserState(USER_STATE.ON_CALL)
        setTimeout(() => {
          keypadInputRef.current?.focus()
        }, 2000)
      })
      conn.on('disconnect', () => {
        resetCallState()
      })
      conn.on('reject', () => {
        resetCallState()
      })
      conn.on('cancel', () => {
        resetCallState()
      })
    } catch (error) {
      console.error('Error initiating call:', error)
      resetCallState()
    }
  }

  const handleCancel = () => {
    cleanupDevice()
    if (onClose) {
      onClose()
    }
  }

  const renderCallButtons = () => {
    if (userState === USER_STATE.IDLE) {
      return (
        <>
          <button
            ref={node => {
              node?.focus()
            }}
            className={style.CallNowButton}
            onClick={handleCall}
            aria-label={`Call now`}
          >
            Call Now
          </button>
          <button className={style.CancelButton} onClick={handleCancel} aria-label='Cancel'>
            Cancel
          </button>
        </>
      )
    }

    if (userState === USER_STATE.ON_CALL) {
      return (
        <button
          ref={node => {
            node?.focus()
          }}
          className={style.EndCallButton}
          onClick={handleCall}
          aria-label='End call'
        >
          End Call
        </button>
      )
    }

    return (
      <button
        ref={node => {
          node?.focus()
        }}
        className={style.CancelButton}
        onClick={handleCancel}
        aria-label='Cancel call'
      >
        Cancel Call
      </button>
    )
  }

  return (
    <ModalOverlay
      className={style.PhoneDialogOverlay}
      isDismissable={false}
      isOpen
      onOpenChange={isOpen => !isOpen && onClose?.()}
    >
      <Modal>
        <Dialog className={style.PhoneDialogBox} aria-labelledby='phone-status'>
          <div className={style.PhoneDisplay}>
            <div className={style.PhoneNumber} aria-hidden='true'>
              🛒{phoneInfo.displayNumber}
            </div>
            {userState === USER_STATE.ON_CALL && <Timer />}
          </div>

          <h2 id='phone-status' className={style.StatusBar} aria-live='assertive'>
            {userState === USER_STATE.IDLE && 'Ready to Call'}
            {userState === USER_STATE.CALLING && 'Connecting Call...'}
            {userState === USER_STATE.ON_CALL && 'Connected with Shopping Assistant'}
          </h2>

          {userState === USER_STATE.ON_CALL && (
            <Keypad callRef={connectionRef} inputRef={keypadInputRef} />
          )}
          <div className={style.CallActions}>{renderCallButtons()}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
})
